import { BulkPrintRequestResource } from '@appTypes/PortfolioBulkActionResource';
import { PortfolioResource } from '@appTypes/PortfolioResource';
import { PrintDataBulk } from '@appTypes/PrintData';
import ButtonComponent from '@components/shared/button/ButtonComponent';
import ConfirmationModal from '@components/shared/modals/confirmation-modal/ConfirmationModal';
import { PopupBlockedIcon } from '@components/style/PopupBlockedIcon';
import { Typography } from '@components/style/Typography';
import { useRouterService } from '@hooks/common/UseRouterService';
import { usePortfolioBulkActionsContainer } from '@hooks/UsePortfolioBulkActionsContainer';
import { AMCustomToast, DismissAllToasts, ErrorToast, InfoToast } from '@services/utils/ToastUtils';
import { AppState } from '@store/AppState';
import { setIsPortfolioBulkPrintInPorgress } from '@store/portfolio/PortfolioActions';
import { PortfolioState } from '@store/portfolio/PortfolioReducer';
import { theme } from '@theme';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type PrintOfferProps = {
  selectedRecords: Array<PortfolioResource>;
  selectedRecordsCount: number;
  totalRecordsCount: number;
  isAllRowsSelected: boolean;
  isClearSelectionLink: boolean;
  toggleSelectedRows: (value: boolean) => void;
};

const PrintOffer: FC<PrintOfferProps> = (props) => {
  const [isPrintOfferModalOpen, setIsPrintOfferModalOpen] = useState<boolean>(false);
  const { createBulkPrintPayload, bulkPrintOffer } = usePortfolioBulkActionsContainer();
  const { isPortfolioBulkPrintInPorgress } = useSelector<AppState, PortfolioState>(
    (state) => state.portfolio,
  );
  const [isExcessRowsSelected, setIsExcessRowsSelected] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { goToBulkPrint } = useRouterService();

  useEffect(() => {
    setIsExcessRowsSelected(
      props.selectedRecordsCount > 25 || (props.isClearSelectionLink && props.isAllRowsSelected),
    );
  }, [props.selectedRecordsCount, props.isClearSelectionLink, props.isAllRowsSelected]);

  const openPrintOfferModal = (): void => {
    setIsPrintOfferModalOpen(true);
  };

  const handleCancel = () => {
    setIsPrintOfferModalOpen(false);
  };

  const getPopupBlockedToastMessage = (): JSX.Element => {
    return (
      <>
        <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.normal}>
          Your selected deal sheets are being prepared; this may take up to a minute. Pop-ups must
          be enabled in the Address Bar
        </Typography>
        <PopupBlockedIcon />
      </>
    );
  };

  const handleSubmit = (): void => {
    AMCustomToast(getPopupBlockedToastMessage(), false, true);
    props.toggleSelectedRows(false);

    let payload: BulkPrintRequestResource = createBulkPrintPayload(
      props.isAllRowsSelected,
      props.isClearSelectionLink,
      props.selectedRecords,
      25,
    );
    const successCallback = (response: PrintDataBulk[]) => {
      let bulkPrintOfferResponse = {
        printData: response,
      };

      localStorage.setItem('portfolioBulkPrint', JSON.stringify(bulkPrintOfferResponse));
      var successRecordsCount = response?.filter(
        (printData) => printData.isDataLoaded === true,
      )?.length;

      if (successRecordsCount && successRecordsCount > 0) {
        goToBulkPrint().then((isPopupBlocked: boolean) => {
          DismissAllToasts();
          if (isPopupBlocked) {
            localStorage.setItem('portfolioBulkPrint', '');
            ErrorToast(
              'Pop-ups are currently disabled in your browser. Please select “Always Allow” and download the deal sheets again to enable the new tab to open.',
              false,
            );
          }
          if (response?.find((printData) => printData.isDataLoaded === false)) {
            var failedRecordsCustomerNames = response
              ?.filter((printData) => printData.isDataLoaded === false)
              ?.map((printData) => printData.customerName)
              ?.join(', ');
            InfoToast(
              `${successRecordsCount} out of ${response?.length} records prepared to print. Failed records: ${failedRecordsCustomerNames}`,
              false,
              true,
            );
          }
          dispatch(setIsPortfolioBulkPrintInPorgress(false));
        });
      } else {
        DismissAllToasts();
        localStorage.setItem('portfolioBulkPrint', '');
        dispatch(setIsPortfolioBulkPrintInPorgress(false));
        ErrorToast(
          'There was an error in producing the deal sheets you requested. Please try again.',
          false,
        );
      }
    };
    setIsPrintOfferModalOpen(false);
    bulkPrintOffer(payload, successCallback);
  };

  const bodyComponent: JSX.Element = (
    <>
      <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.semibold}>
        You are about to prepare {isExcessRowsSelected ? 25 : props.selectedRecordsCount} deal
        sheets from your selected customer <br></br>list to print.
      </Typography>

      <Typography size={theme.typography.h5}>
        &nbsp;You can prepare 25 deal sheets at a time. When complete,<br></br> a new tab will open
        in your browser.
      </Typography>
    </>
  );

  const restrictPrintOfferMessage: JSX.Element = (
    <>
      <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.semibold}>
        Only 25 records can be printed at a time.
      </Typography>
      <Typography size={theme.typography.h5} marginTop={12} display={'block'}>
        Please select 25 or fewer records.
      </Typography>
    </>
  );

  return (
    <>
      <ButtonComponent
        color={isPortfolioBulkPrintInPorgress ? theme.palette.dusty : theme.palette.mediumGray}
        wrapperBackgroundColor={theme.palette.blackOlive}
        text={isPortfolioBulkPrintInPorgress ? 'Printing...' : 'Print Offers...'}
        fontWeight={theme.typography.fontWeights.semibold}
        outerPadding={'0px'}
        innerPadding={'7px 12px'}
        borderRadius={4}
        onClick={openPrintOfferModal}
        backgroundColor={theme.palette.white}
        disabled={isPortfolioBulkPrintInPorgress}
        textAlign='left'
      />
      <ConfirmationModal
        isOpen={isPrintOfferModalOpen}
        handleSubmitClick={handleSubmit}
        handleCancelClick={handleCancel}
        cancelButtonText='Cancel'
        submitButtonText='Prepare for Print'
        title='Bulk Deal Sheet Print'
        bodyComponent={isExcessRowsSelected ? restrictPrintOfferMessage : bodyComponent}
        showCloseIcon={false}
        padding={'20px 20px 20px 15px'}
        minHeight={'225px'}
        minWidth={'465px'}
        submitButtonBackgroundColor={
          isExcessRowsSelected ? theme.palette.backgroundGray : theme.palette.purple
        }
        submitButtonTextColor={
          isExcessRowsSelected ? theme.palette.lightGreyTwo : theme.palette.white
        }
        submitButtonDisabled={isExcessRowsSelected}
        shouldCloseOnOverlayClick
      />
    </>
  );
};

export default PrintOffer;
